// useAddDatabaseModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Toast
import { toast } from 'react-toastify';

// Context
import { useCollection } from 'context/DatabaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { toLowerSnakeCase } from 'utilities/Strings';
import { buildCollectionPath } from 'utilities/collectionPaths';

// Services
import { updateNodeTypeMeta } from 'services/nodetypes/NodeTypeMutationService';

// Hooks
import { useCreateTypeForm } from 'components/addmodal/sections/createtype/useCreateTypeForm';
import { useLibrarySection } from 'components/addmodal/sections/library/useLibrarySection';
import { LEGACY_LIBRARY_UI_ENABLED } from 'legacy/legacyFeatureFlags';

export const STEP_LANDING = 'landing';
export const STEP_TEMPLATE = 'template';
export const STEP_CREATE = 'create';
export const STEP_FIELDS = 'fields';

export const CREATE_TAB_ABOUT = 'about';
export const CREATE_TAB_FIELDS = 'fields';

const LIBRARY_TAB = 'template';

const INITIAL_STEP = LEGACY_LIBRARY_UI_ENABLED ? STEP_LANDING : STEP_CREATE;

const buildDefaultDatabaseKey = (baseKeyValue, typeKeyValue) => (
    `${baseKeyValue}_${typeKeyValue}_all`.replace(/\//g, '_')
);

/**
 * useAddDatabaseModalState
 *
 * Manages wizard flow state, library provisioning, and create-type editing for AddDatabaseModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onClose - Called when the modal is dismissed.
 * @param {function} [params.onCreated] - Called when the modal closes after a database was created.
 * @param {string|null} params.baseKey - Active base key.
 *
 * @returns {object} Grouped modal state and handlers for the AddDatabaseModal shell.
 */
export default function useAddDatabaseModalState({
    isOpen,
    onClose,
    onCreated,
    baseKey,
}) {
    const { setActiveCollection: setActiveDatabase } = useCollection();
    const { getAll } = useNodeTypes();
    const { user } = useUser();
    const navigate = useNavigate();

    const [step, setStep] = useState(INITIAL_STEP);
    const [newTypeKey, setNewTypeKey] = useState('');
    const [isUpdatingType, setIsUpdatingType] = useState(false);

    const typeDefs = useMemo(() => getAll() || [], [getAll]);
    const userKey = user?.key || null;

    /**
     * handleClose
     *
     * Resets the modal wizard state and notifies the parent that the modal should close.
     */
    const handleClose = useCallback(() => {
        const completed = Boolean(String(newTypeKey || '').trim());
        setStep(INITIAL_STEP);
        setNewTypeKey('');
        setIsUpdatingType(false);
        onClose?.();
        if (completed) onCreated?.();
    }, [newTypeKey, onClose, onCreated]);

    useEffect(() => {
        if (!isOpen) return;
        setStep(INITIAL_STEP);
        setNewTypeKey('');
        setIsUpdatingType(false);
    }, [isOpen]);

    /**
     * handleProvisionedTypeCreated
     *
     * Activates a newly provisioned template database and navigates to it.
     *
     * @param {string} typeKey - Newly created node type key.
     * @param {object} createdType - Created type metadata returned from provisioning.
     */
    const handleProvisionedTypeCreated = useCallback((typeKey, createdType) => {
        const resolvedTypeKey = String(typeKey || '').trim();
        if (!resolvedTypeKey || !baseKey) {
            handleClose();
            return;
        }

        const databaseKey = buildDefaultDatabaseKey(baseKey, resolvedTypeKey);
        const databaseTitle = String(createdType?.labelPlural || '').trim() || 'Items';

        setActiveDatabase?.({
            key: databaseKey,
            baseKey,
            type: resolvedTypeKey,
            title: databaseTitle,
            userKey,
            filters: [],
        });

        setStep(INITIAL_STEP);
        setNewTypeKey('');
        setIsUpdatingType(false);
        onClose?.();
        onCreated?.();
        navigate(buildCollectionPath(databaseKey));
    }, [handleClose, navigate, onClose, onCreated, setActiveDatabase, userKey, baseKey]);

    /**
     * handleTypeCreated
     *
     * Stores the newly created type key and advances the wizard into the fields step.
     *
     * @param {string} typeKey - New type key created by the create-type flow.
     */
    const handleTypeCreated = useCallback((typeKey) => {
        setNewTypeKey(typeKey);
        setStep(STEP_FIELDS);
    }, []);

    const {
        query: libraryQuery,
        setQuery: setLibraryQuery,
        loading: libraryLoading,
        error: libraryError,
        terms: libraryTerms,
        selectedTerm: librarySelectedTerm,
        selectedAddon: librarySelectedAddon,
        isAddonStep: libraryIsAddonStep,
        isAddonPreviewStep: libraryIsAddonPreviewStep,
        isProvisioning: libraryIsProvisioning,
        provisioningAddonKey,
        selectedFieldKeys: librarySelectedFieldKeys,
        baseKey: libraryBaseKey,
        handleSelectTerm,
        handleSelectAddon,
        handleBackToTerms,
        handleBackToAddons,
        handleToggleSelectedField,
        handleUseSelectedAddon,
    } = useLibrarySection({
        isOpen,
        selectedTab: step,
        libraryTabValue: LIBRARY_TAB,
        baseKey,
        availableTypes: typeDefs,
        onTypeSelect: handleProvisionedTypeCreated,
        onClose: () => {},
    });

    /**
     * handleTemplateBack
     *
     * Steps backward through the template provisioning flow or returns to the landing step.
     */
    const handleTemplateBack = useCallback(() => {
        if (libraryIsAddonPreviewStep) {
            handleBackToAddons();
            return;
        }

        if (libraryIsAddonStep) {
            handleBackToTerms();
            return;
        }

        setStep(INITIAL_STEP);
    }, [libraryIsAddonPreviewStep, libraryIsAddonStep, handleBackToTerms, handleBackToAddons]);

    const isCreateFlow = step === STEP_CREATE || step === STEP_FIELDS;
    const hasCreatedType = Boolean(String(newTypeKey || '').trim());

    const {
        label,
        setLabel,
        labelPlural,
        description,
        setDescription,
        iconKey,
        setIconKey,
        defaultView,
        setDefaultView,
        isSavingType,
        canSaveType,
        labelHelperText,
        pluralHelperText,
        handleLabelPluralChange,
        handleLabelBlur,
        handleLabelPluralBlur,
        handleCreateType,
    } = useCreateTypeForm({
        isOpen: isOpen && isCreateFlow,
        typeDefs,
        excludeTypeKey: newTypeKey,
        userKey,
        baseKey,
        onTypeSelect: handleTypeCreated,
        onClose: () => {},
    });

    const createTabs = useMemo(() => {
        const tabs = [
            {
                key: CREATE_TAB_ABOUT,
                label: 'About',
                onClick: () => setStep(STEP_CREATE),
            },
        ];

        if (hasCreatedType) {
            tabs.push({
                key: CREATE_TAB_FIELDS,
                label: 'Fields',
                onClick: () => setStep(STEP_FIELDS),
            });
        }

        return tabs;
    }, [hasCreatedType]);

    const selectedCreateTab = step === STEP_FIELDS ? CREATE_TAB_FIELDS : CREATE_TAB_ABOUT;

    const canSaveCreatedType = useMemo(() => {
        return hasCreatedType
            && Boolean(String(label || '').trim())
            && !labelHelperText
            && !pluralHelperText
            && !isSavingType
            && !isUpdatingType;
    }, [hasCreatedType, isSavingType, isUpdatingType, label, labelHelperText, pluralHelperText]);

    /**
     * handleSaveCreatedType
     *
     * Persists metadata updates for a newly created database type.
     *
     * @returns {Promise<void>} Resolves when the save flow completes.
     */
    const handleSaveCreatedType = useCallback(async () => {
        const cleanedTypeKey = String(newTypeKey || '').trim();
        const cleanedLabel = String(label || '').trim();
        const cleanedPlural = String(labelPlural || '').trim() || cleanedLabel;

        if (!cleanedTypeKey) return;
        if (!cleanedLabel) {
            toast.error('Database name is required.');
            return;
        }

        setIsUpdatingType(true);

        try {
            await updateNodeTypeMeta({
                baseKey: baseKey || null,
                typeKey: cleanedTypeKey,
                label: cleanedLabel,
                labelPlural: cleanedPlural,
                description,
                iconKey: toLowerSnakeCase(iconKey),
                defaultView,
            });
            toast.success('Database details updated.');
        } catch (error) {
            toast.error(error?.message || 'Could not update database details.');
        } finally {
            setIsUpdatingType(false);
        }
    }, [defaultView, description, iconKey, label, labelPlural, newTypeKey, baseKey]);

    const createdTypeLabel = useMemo(() => {
        const createdType = typeDefs.find((candidate) => String(candidate?.key || '').trim() === String(newTypeKey || '').trim());
        const sourceLabel = String(createdType?.label || label || '').trim();
        return sourceLabel ? sourceLabel : 'Item';
    }, [label, newTypeKey, typeDefs]);

    const createdTypeLabelLower = useMemo(() => {
        return createdTypeLabel.toLowerCase();
    }, [createdTypeLabel]);

    const modalTitle = hasCreatedType ? createdTypeLabel : 'Add Collection';

    return {
        flowState: {
            isLandingStep: step === STEP_LANDING,
            isTemplateStep: step === STEP_TEMPLATE,
            isCreateStep: step === STEP_CREATE,
            isFieldsStep: step === STEP_FIELDS,
            isCreateFlow,
            hasCreatedType,
            modalTitle,
            createTabs,
            selectedCreateTab,
            createdTypeLabelLower,
        },
        libraryState: {
            query: libraryQuery,
            setQuery: setLibraryQuery,
            loading: libraryLoading,
            error: libraryError,
            terms: libraryTerms,
            selectedTerm: librarySelectedTerm,
            selectedAddon: librarySelectedAddon,
            isAddonStep: libraryIsAddonStep,
            isAddonPreviewStep: libraryIsAddonPreviewStep,
            isProvisioning: libraryIsProvisioning,
            provisioningAddonKey,
            selectedFieldKeys: librarySelectedFieldKeys,
            baseKey: libraryBaseKey,
            handleSelectTerm,
            handleSelectAddon,
            handleToggleSelectedField,
            handleUseSelectedAddon,
            handleTemplateBack,
        },
        createTypeState: {
            newTypeKey,
            label,
            setLabel,
            labelPlural,
            description,
            setDescription,
            iconKey,
            setIconKey,
            defaultView,
            setDefaultView,
            isSavingType,
            isUpdatingType,
            canSaveType,
            canSaveCreatedType,
            labelHelperText,
            pluralHelperText,
            handleLabelPluralChange,
            handleLabelBlur,
            handleLabelPluralBlur,
            handleCreateType,
            handleSaveCreatedType,
        },
        modalActions: {
            handleClose,
            openCreateFlow: () => setStep(STEP_CREATE),
            openTemplateFlow: () => {
                if (!LEGACY_LIBRARY_UI_ENABLED) return;
                setStep(STEP_TEMPLATE);
            },
            returnToLanding: () => {
                if (LEGACY_LIBRARY_UI_ENABLED) {
                    setStep(STEP_LANDING);
                    return;
                }
                handleClose();
            },
        },
    };
}